import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../firebase/AuthContext';
import UserHeader from './UserHeader';
import MonthlyTitleView from './MonthlyTitleView';
import './MantraAdmin.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const TOPIC_CATEGORY_LABELS = {
  work: '仕事・キャリア',
  family: '家族',
  relationship: '人間関係・恋愛',
  health: '健康・身体',
  mental: 'メンタル・感情',
  learning: '学び・スキル',
  hobby: '趣味・娯楽',
  money: 'お金・資産',
  parenting: '子育て',
  daily_life: '日常・生活',
  technology: 'IT・ツール',
  creativity: '創作・アイデア',
  community: '社会・コミュニティ',
  goal: '目標・計画',
};

const RATING_FIELDS = [
  { key: 'phase_tags', label: '思考フェーズ (phase_tags)', ratingKey: 'phase_tags_rating' },
  { key: 'topic_tags', label: '話題タグ (topic_tags)', ratingKey: 'topic_tags_rating' },
  { key: 'event_signature', label: '出来事署名 (event_signature)', ratingKey: 'event_signature_rating' },
];

const FIELD_FILTER_PREFIX = {
  phase_tags: 'phase',
  topic_tags: 'topic',
  event_signature: 'event',
};

const formatDateTime = (dateTimeStr) => {
  if (!dateTimeStr) return '';
  try {
    const date = new Date(dateTimeStr);
    if (Number.isNaN(date.getTime())) return dateTimeStr;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  } catch {
    return dateTimeStr;
  }
};

const getItemRatings = (item) => item?.ratings || {};

const isFullyRated = (item) =>
  RATING_FIELDS.every(({ ratingKey }) => {
    const v = getItemRatings(item)[ratingKey];
    return v >= 1 && v <= 5;
  });

const hasAnyUnrated = (item) => !isFullyRated(item);

const FilterChip = ({ value, current, onSelect, children }) => (
  <button
    type="button"
    className={`mantra-admin-filter-chip${current === value ? ' mantra-admin-filter-chip-active' : ''}`}
    onClick={() => onSelect(value)}
    aria-pressed={current === value}
  >
    {children}
  </button>
);

const FilterGroup = ({ title, description, children }) => (
  <fieldset className="mantra-admin-filter-group">
    <legend>{title}</legend>
    {description ? <p className="mantra-admin-filter-group-desc">{description}</p> : null}
    <div className="mantra-admin-filter-chips">{children}</div>
  </fieldset>
);

const StarRatingRow = ({ label, value, onRate, onClear, existingLabel }) => (
  <div className="mantra-admin-rating-block">
    <h4>{label}</h4>
    {existingLabel && value ? (
      <div className="mantra-admin-existing-rating">{existingLabel}</div>
    ) : null}
    <div className="mantra-admin-star-rating">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          className={`mantra-admin-star-btn ${value === star ? 'mantra-admin-active' : ''}`}
          onClick={() => onRate(star)}
          aria-label={`${label} ${star}点`}
        >
          {value && value >= star ? '★' : '☆'}
        </button>
      ))}
    </div>
    {value ? (
      <>
        <p className="mantra-admin-rating-text">評価: {value}/5</p>
        <button type="button" className="mantra-admin-clear-rating-btn" onClick={onClear}>
          評価をクリア
        </button>
      </>
    ) : null}
  </div>
);

const PhaseTagsView = ({ tags }) => {
  if (!tags?.length) return <p className="mantra-admin-empty-ai">（未付与）</p>;
  return (
    <div className="mantra-admin-tags-list">
      {tags.map((tagItem, idx) => (
        <div key={idx} className="mantra-admin-tag-item">
          <img
            src={`/img/phase-tags/${tagItem.tag}.png`}
            alt={tagItem.tag}
            className="mantra-admin-tag-image"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
          <span className="mantra-admin-tag-label">{tagItem.tag}</span>
          {tagItem.reason ? (
            <span className="mantra-admin-tag-reason">{tagItem.reason}</span>
          ) : null}
        </div>
      ))}
    </div>
  );
};

const TopicTagsView = ({ tags }) => {
  if (!tags?.length) return <p className="mantra-admin-empty-ai">（未付与）</p>;
  return (
    <div className="mantra-admin-tags-list">
      {tags.map((tagItem, idx) => (
        <div key={idx} className="mantra-admin-tag-item mantra-admin-topic-tag-item">
          <span className="mantra-admin-tag-label">{tagItem.tag}</span>
          {tagItem.reason ? (
            <span className="mantra-admin-tag-reason">{tagItem.reason}</span>
          ) : null}
        </div>
      ))}
    </div>
  );
};

const TopicScopeView = ({ categories, subjects }) => {
  const hasCategories = categories?.length > 0;
  const hasSubjects = subjects?.length > 0;
  if (!hasCategories && !hasSubjects) {
    return <p className="mantra-admin-empty-ai">（未付与）</p>;
  }
  return (
    <div className="mantra-admin-topic-scope">
      <div className="mantra-admin-topic-scope-section">
        <p className="mantra-admin-topic-scope-heading">カテゴリ (topic_categories)</p>
        {hasCategories ? (
          <div className="mantra-admin-tags-list">
            {categories.map((tagItem, idx) => (
              <div key={idx} className="mantra-admin-tag-item mantra-admin-topic-category-item">
                <span className="mantra-admin-tag-label">{tagItem.tag}</span>
                {TOPIC_CATEGORY_LABELS[tagItem.tag] ? (
                  <span className="mantra-admin-tag-category-label">
                    {TOPIC_CATEGORY_LABELS[tagItem.tag]}
                  </span>
                ) : null}
                {tagItem.reason ? (
                  <span className="mantra-admin-tag-reason">{tagItem.reason}</span>
                ) : null}
              </div>
            ))}
          </div>
        ) : (
          <p className="mantra-admin-empty-ai">（未付与）</p>
        )}
      </div>
      <div className="mantra-admin-topic-scope-section">
        <p className="mantra-admin-topic-scope-heading">話題対象 (topic_subjects)</p>
        {hasSubjects ? (
          <div className="mantra-admin-tags-list">
            {subjects.map((tagItem, idx) => (
              <div key={idx} className="mantra-admin-tag-item mantra-admin-topic-tag-item">
                <span className="mantra-admin-tag-label">{tagItem.tag}</span>
                {tagItem.reason ? (
                  <span className="mantra-admin-tag-reason">{tagItem.reason}</span>
                ) : null}
              </div>
            ))}
          </div>
        ) : (
          <p className="mantra-admin-empty-ai">（未付与）</p>
        )}
      </div>
    </div>
  );
};

const EventSignatureView = ({ eventSignature }) => {
  if (!eventSignature || Object.keys(eventSignature).length === 0) {
    return <p className="mantra-admin-empty-ai">（未付与）</p>;
  }
  const es = eventSignature;
  if (es.extractable === false) {
    return (
      <div className="mantra-admin-event-signature">
        <p><strong>extractable:</strong> false</p>
        {es.confidence != null ? <p><strong>confidence:</strong> {es.confidence}</p> : null}
        <p className="mantra-admin-empty-ai">出来事の骨格が抽出されませんでした</p>
      </div>
    );
  }
  return (
    <div className="mantra-admin-event-signature">
      {es.event_type ? <p><strong>event_type:</strong> {es.event_type}</p> : null}
      {Array.isArray(es.actors) && es.actors.length > 0 ? (
        <p><strong>actors:</strong> {es.actors.join(' / ')}</p>
      ) : null}
      {Array.isArray(es.constraints) && es.constraints.length > 0 ? (
        <p><strong>constraints:</strong> {es.constraints.join(' / ')}</p>
      ) : null}
      {es.outcome_state ? <p><strong>outcome_state:</strong> {es.outcome_state}</p> : null}
      {es.event_signature_text ? (
        <p><strong>event_signature_text:</strong> {es.event_signature_text}</p>
      ) : null}
      {es.confidence != null ? <p><strong>confidence:</strong> {es.confidence}</p> : null}
    </div>
  );
};

const RatingStatistics = ({ fieldKey, label, stats }) => {
  if (!stats) return null;
  return (
    <div className="mantra-admin-stat-block">
      <h3>{label}</h3>
      <div className="mantra-admin-stat-item">
        <span>評価済み: {stats.rated_count}件</span>
        <span>平均: {stats.average_rating}/5.0</span>
      </div>
      <div className="mantra-admin-rating-distribution">
        {[5, 4, 3, 2, 1].map((r) => (
          <div key={r} className="mantra-admin-dist-item">
            {r}点: {'★'.repeat(stats.rating_distribution?.[r] || 0)} (
            {stats.rating_distribution?.[r] || 0}件)
          </div>
        ))}
      </div>
    </div>
  );
};

const MantraAdmin = () => {
  const { sessionId } = useAuth();
  const [viewMode, setViewMode] = useState('main');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [minRating, setMinRating] = useState(1);
  const [statistics, setStatistics] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const authHeaders = useCallback(
    () => ({
      Authorization: `Bearer ${sessionId}`,
      'Content-Type': 'application/json',
    }),
    [sessionId]
  );

  const fetchItems = useCallback(async () => {
    try {
      if (!sessionId) {
        alert('ログインが必要です');
        return;
      }
      const response = await fetch(
        `${API_BASE_URL}/admin/comment-enrichment-items?email_prefix=reibow`,
        { method: 'GET', headers: authHeaders() }
      );
      if (!response.ok) throw new Error('コメント一覧取得に失敗しました');
      const data = await response.json();
      setItems(data.items || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching comment items:', error);
      alert(`コメント一覧取得に失敗しました: ${error.message}`);
      setLoading(false);
    }
  }, [sessionId, authHeaders]);

  const fetchStatistics = useCallback(async () => {
    try {
      if (!sessionId) return;
      const response = await fetch(`${API_BASE_URL}/admin/comment-enrichment-statistics`, {
        method: 'GET',
        headers: authHeaders(),
      });
      if (response.ok) {
        setStatistics(await response.json());
      }
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  }, [sessionId, authHeaders]);

  useEffect(() => {
    if (viewMode === 'main') {
      setLoading(true);
      fetchItems();
      fetchStatistics();
    }
  }, [viewMode, fetchItems, fetchStatistics]);

  const getFilteredItems = useCallback(() => {
    let filtered = [...items];

    if (filter === 'unrated') {
      filtered = filtered.filter(hasAnyUnrated);
    } else if (filter === 'rated') {
      filtered = filtered.filter(isFullyRated);
    } else if (filter === 'minRating-phase') {
      filtered = filtered.filter(
        (item) => (getItemRatings(item).phase_tags_rating || 0) >= minRating
      );
    } else if (filter.startsWith('phase-rating-')) {
      const target = parseInt(filter.replace('phase-rating-', ''), 10);
      filtered = filtered.filter((item) => getItemRatings(item).phase_tags_rating === target);
    } else if (filter.startsWith('topic-rating-')) {
      const target = parseInt(filter.replace('topic-rating-', ''), 10);
      filtered = filtered.filter((item) => getItemRatings(item).topic_tags_rating === target);
    } else if (filter.startsWith('event-rating-')) {
      const target = parseInt(filter.replace('event-rating-', ''), 10);
      filtered = filtered.filter(
        (item) => getItemRatings(item).event_signature_rating === target
      );
    } else if (filter === 'phase-unrated') {
      filtered = filtered.filter((item) => !getItemRatings(item).phase_tags_rating);
    } else if (filter === 'topic-unrated') {
      filtered = filtered.filter((item) => !getItemRatings(item).topic_tags_rating);
    } else if (filter === 'event-unrated') {
      filtered = filtered.filter((item) => !getItemRatings(item).event_signature_rating);
    }

    return filtered;
  }, [items, filter, minRating]);

  const filteredItems = getFilteredItems();
  const currentItem = filteredItems[currentIndex];

  useEffect(() => {
    if (currentIndex >= filteredItems.length) {
      setCurrentIndex(Math.max(0, filteredItems.length - 1));
    }
  }, [filteredItems.length, currentIndex]);

  const updateLocalRating = (docId, fieldKey, ratingValue) => {
    const ratingKey = `${fieldKey}_rating`;
    setItems((prev) =>
      prev.map((item) =>
        item.doc_id === docId
          ? {
              ...item,
              ratings: {
                ...getItemRatings(item),
                [ratingKey]: ratingValue,
              },
            }
          : item
      )
    );
  };

  const saveRating = async (docId, fieldKey, ratingValue) => {
    try {
      if (!sessionId) {
        alert('ログインが必要です');
        return;
      }
      const response = await fetch(`${API_BASE_URL}/admin/comment-enrichment-rating`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          doc_id: docId,
          field: fieldKey,
          rating: ratingValue,
        }),
      });
      if (!response.ok) throw new Error('評価の保存に失敗しました');
      updateLocalRating(docId, fieldKey, ratingValue);
      fetchStatistics();
    } catch (error) {
      console.error('Error saving rating:', error);
      alert(`評価の保存に失敗しました: ${error.message}`);
    }
  };

  const clearRating = async (docId, fieldKey) => {
    if (!window.confirm('この項目の評価をクリアしますか？')) return;
    try {
      if (!sessionId) {
        alert('ログインが必要です');
        return;
      }
      const response = await fetch(
        `${API_BASE_URL}/admin/comment-enrichment-rating/${encodeURIComponent(docId)}?field=${encodeURIComponent(fieldKey)}`,
        { method: 'DELETE', headers: authHeaders() }
      );
      if (!response.ok) throw new Error('評価の削除に失敗しました');
      updateLocalRating(docId, fieldKey, null);
      fetchStatistics();
    } catch (error) {
      console.error('Error clearing rating:', error);
      alert(`評価の削除に失敗しました: ${error.message}`);
    }
  };

  const handleNext = () => {
    if (currentIndex < filteredItems.length - 1) setCurrentIndex(currentIndex + 1);
  };

  const handlePrev = () => {
    if (currentIndex > 0) setCurrentIndex(currentIndex - 1);
  };

  const setFilterAndReset = (value) => {
    setFilter(value);
    setCurrentIndex(0);
  };

  if (viewMode === 'monthly_title') {
    return <MonthlyTitleView onBack={() => setViewMode('main')} />;
  }

  if (loading) {
    return (
      <div className="mantra-admin">
        <UserHeader />
        <div className="mantra-admin-loading">読み込み中...</div>
      </div>
    );
  }

  return (
    <div className="mantra-admin">
      <UserHeader />
      <div className="mantra-admin-container">
        <div className="mantra-admin-header">
          <h1>コメント AI 付与 評価管理</h1>
          <div className="mantra-admin-header-actions">
            <Link to="/topic-tags-aggregate" className="mantra-admin-link-btn">
              話題タグ集計
            </Link>
            <button
              type="button"
              className="mantra-admin-link-btn"
              onClick={() => setViewMode('monthly_title')}
            >
              月次タイトル一覧を見る
            </button>
          </div>
        </div>

        {statistics && (
          <div className="mantra-admin-statistics">
            <h2>評価統計</h2>
            <div className="mantra-admin-stat-grid">
              {RATING_FIELDS.map(({ key, label }) => (
                <RatingStatistics
                  key={key}
                  fieldKey={key}
                  label={label}
                  stats={statistics[key]}
                />
              ))}
            </div>
          </div>
        )}

        <div className="mantra-admin-filter-section">
          <h2 className="mantra-admin-filter-heading">表示フィルタ</h2>

          <FilterGroup title="表示範囲" description="コメント全体の評価状況で絞り込み">
            <FilterChip value="all" current={filter} onSelect={setFilterAndReset}>
              すべて
            </FilterChip>
            <FilterChip value="unrated" current={filter} onSelect={setFilterAndReset}>
              未評価あり
            </FilterChip>
            <FilterChip value="rated" current={filter} onSelect={setFilterAndReset}>
              3項目すべて評価済み
            </FilterChip>
          </FilterGroup>

          <FilterGroup title="項目別：未評価" description="特定の AI 付与項目だけ未評価のコメント">
            {RATING_FIELDS.map(({ key, label }) => (
              <FilterChip
                key={`${key}-unrated`}
                value={`${FIELD_FILTER_PREFIX[key]}-unrated`}
                current={filter}
                onSelect={setFilterAndReset}
              >
                {label.split(' ')[0]}
              </FilterChip>
            ))}
          </FilterGroup>

          <div className="mantra-admin-filter-group mantra-admin-filter-group-rating">
            <fieldset>
              <legend>項目別：評価点（完全一致）</legend>
              <p className="mantra-admin-filter-group-desc">各項目の星評価と一致するコメントのみ表示</p>
              {RATING_FIELDS.map(({ key, label }) => (
                <div key={key} className="mantra-admin-filter-rating-row">
                  <span className="mantra-admin-filter-rating-label">{label.split(' ')[0]}</span>
                  <div className="mantra-admin-filter-chips">
                    {[1, 2, 3, 4, 5].map((rating) => (
                      <FilterChip
                        key={rating}
                        value={`${FIELD_FILTER_PREFIX[key]}-rating-${rating}`}
                        current={filter}
                        onSelect={setFilterAndReset}
                      >
                        {rating}点
                      </FilterChip>
                    ))}
                  </div>
                </div>
              ))}
            </fieldset>
          </div>

          <FilterGroup title="phase_tags：評価以上">
            <div className="mantra-admin-filter-min-row">
              <FilterChip value="minRating-phase" current={filter} onSelect={setFilterAndReset}>
                {minRating}点以上
              </FilterChip>
              <select
                className="mantra-admin-filter-min-select"
                value={minRating}
                onChange={(e) => {
                  setMinRating(parseInt(e.target.value, 10));
                  setFilterAndReset('minRating-phase');
                }}
                aria-label="phase_tags 最低評価"
              >
                {[1, 2, 3, 4, 5].map((r) => (
                  <option key={r} value={r}>
                    {r}点
                  </option>
                ))}
              </select>
            </div>
          </FilterGroup>
        </div>

        {filteredItems.length === 0 ? (
          <div className="mantra-admin-no-posters">表示するコメントがありません</div>
        ) : (
          <>
            <div className="mantra-admin-poster-counter">
              {currentIndex + 1} / {filteredItems.length}
            </div>

            {currentItem && (
              <div className="mantra-admin-poster-card">
                <div className="mantra-admin-navigation-top">
                  <button type="button" onClick={handlePrev} disabled={currentIndex === 0}>
                    前へ
                  </button>
                  <button
                    type="button"
                    onClick={handleNext}
                    disabled={currentIndex === filteredItems.length - 1}
                  >
                    次へ
                  </button>
                </div>

                <div className="mantra-admin-poster-header">
                  <h2>コメントID: {currentItem.doc_id}</h2>
                </div>

                <div className="mantra-admin-poster-info">
                  <p><strong>ポスターID:</strong> {currentItem.poster_id}</p>
                  <p><strong>ユーザーID:</strong> {currentItem.user_id}</p>
                  <p><strong>問いかけ:</strong> {currentItem.question_title || '—'}</p>
                  <p><strong>ポスタータイトル:</strong> {currentItem.poster_title || '—'}</p>
                  {currentItem.created_at ? (
                    <p><strong>コメント作成:</strong> {formatDateTime(currentItem.created_at)}</p>
                  ) : null}
                </div>

                <div className="mantra-admin-comments-section">
                  <h3>コメント</h3>
                  <div className="mantra-admin-comment-item">
                    <h4>{currentItem.comment_title || '（タイトルなし）'}</h4>
                    <p>{currentItem.comment}</p>
                  </div>
                </div>

                <div className="mantra-admin-ai-content">
                  <h3>AI 付与内容</h3>

                  <div className="mantra-admin-ai-block">
                    <strong>思考フェーズ (phase_tags)</strong>
                    <PhaseTagsView tags={currentItem.phase_tags} />
                  </div>

                  {currentItem.comment_summary ? (
                    <div className="mantra-admin-ai-block">
                      <strong>コメント要約 (comment_summary)</strong>
                      <p className="mantra-admin-comment-summary">{currentItem.comment_summary}</p>
                    </div>
                  ) : null}

                  <div className="mantra-admin-ai-block">
                    <strong>話題スコープ (topic_scope)</strong>
                    <TopicScopeView
                      categories={currentItem.topic_categories}
                      subjects={currentItem.topic_subjects}
                    />
                  </div>

                  <div className="mantra-admin-ai-block">
                    <strong>話題タグ (topic_tags)</strong>
                    <TopicTagsView tags={currentItem.topic_tags} />
                  </div>

                  <div className="mantra-admin-ai-block">
                    <strong>出来事署名 (event_signature)</strong>
                    <EventSignatureView eventSignature={currentItem.event_signature} />
                  </div>
                </div>

                <div className="mantra-admin-rating-section">
                  <h3>評価（各項目 1〜5）</h3>
                  {RATING_FIELDS.map(({ key, label, ratingKey }) => {
                    const value = getItemRatings(currentItem)[ratingKey] || null;
                    const existingLabel = value
                      ? `既存評価: ${'★'.repeat(value)}${'☆'.repeat(5 - value)} (${value}/5)`
                      : null;
                    return (
                      <StarRatingRow
                        key={key}
                        label={label}
                        value={value}
                        existingLabel={existingLabel}
                        onRate={(star) => saveRating(currentItem.doc_id, key, star)}
                        onClear={() => clearRating(currentItem.doc_id, key)}
                      />
                    );
                  })}
                </div>

                <div className="mantra-admin-navigation">
                  <button type="button" onClick={handlePrev} disabled={currentIndex === 0}>
                    前へ
                  </button>
                  <button
                    type="button"
                    onClick={handleNext}
                    disabled={currentIndex === filteredItems.length - 1}
                  >
                    次へ
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MantraAdmin;
